import React, { useState, useEffect, createContext, useContext, ReactNode, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import i18n from '@/i18n';
import { trackSignupConversion } from '@/lib/gtagConversion';

type UserRole = 'admin' | 'staff' | 'student' | 'dev' | 'instructor' | 'guest' | 'creator';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRoles: UserRole[];
  loading: boolean;
  needsPasswordChange: boolean;
  needsTermsAcceptance: boolean;
  currentTermsVersionId: string | null;
  currentTermsVersion: number | null;
  isPaused: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (
    email: string, 
    nombre: string,
    apellidoPaterno: string,
    apellidoMaterno: string,
    recaptchaToken?: string,
  ) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  isAdminOrStaff: () => boolean;
  isStudentOrGuest: () => boolean;
  getHighestRole: () => UserRole | null;
  refreshPasswordStatus: () => Promise<void>;
  refreshTermsStatus: () => Promise<void>;
  isOAuthUser: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [needsTermsAcceptance, setNeedsTermsAcceptance] = useState(false);
  const [currentTermsVersionId, setCurrentTermsVersionId] = useState<string | null>(null);
  const [currentTermsVersion, setCurrentTermsVersion] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  
  // Keep navigateRef in sync
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);
  
  // Track if initial load has completed - prevents onAuthStateChange from interfering
  const initialLoadComplete = useRef(false);
  const isMounted = useRef(true);
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Deduplicate SIGNED_IN processing for the same user session
  const lastProcessedSessionRef = useRef<string | null>(null);
  // Track the latest auth event's user id for stale-result guards
  const latestAuthUserIdRef = useRef<string | null>(null);

  // Session timeout duration: 5 hours in milliseconds
  const SESSION_TIMEOUT_MS = 5 * 60 * 60 * 1000;

  // Clear any existing session timeout
  const clearSessionTimeout = () => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
  };

  // Start session timeout - logs user out after 5 hours
  const startSessionTimeout = (sessionCreatedAt: string) => {
    clearSessionTimeout();
    
    const sessionStart = new Date(sessionCreatedAt).getTime();
    const now = Date.now();
    const elapsed = now - sessionStart;
    const remainingTime = SESSION_TIMEOUT_MS - elapsed;

    console.log('[AUTH DEBUG] Session timeout check:', {
      sessionStart: new Date(sessionStart).toISOString(),
      elapsed: Math.round(elapsed / 1000 / 60) + ' minutes',
      remainingTime: Math.round(remainingTime / 1000 / 60) + ' minutes'
    });

    // If session has already exceeded 5 hours, log out immediately
    if (remainingTime <= 0) {
      console.log('[AUTH DEBUG] Session expired (>5 hours), forcing logout');
      handleSessionExpired();
      return;
    }

    // Set timeout for remaining time
    sessionTimeoutRef.current = setTimeout(() => {
      console.log('[AUTH DEBUG] Session timeout reached (5 hours), forcing logout');
      handleSessionExpired();
    }, remainingTime);
  };

  // Handle expired session
  const handleSessionExpired = async () => {
    if (!isMounted.current) return;
    
    console.log('[AUTH DEBUG] Handling session expiration...');
    
    // Clear state first
    setUser(null);
    setSession(null);
    setUserRoles([]);
    setNeedsPasswordChange(false);
    
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.log('[AUTH DEBUG] SignOut during expiration failed, clearing localStorage');
    }
    
    // Clear all Supabase localStorage keys
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Navigate to auth page
    navigateRef.current('/auth', { replace: true });
  };

  const fetchUserRoles = async (userId: string, retryCount = 0): Promise<UserRole[] | null> => {
    console.log('[AUTH DEBUG] Fetching roles for user:', userId, 'attempt:', retryCount + 1);
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (error) {
        console.error('[AUTH DEBUG] Error fetching user roles:', error);
        return null;
      }

      const roles = data?.map((r) => r.role as UserRole) || [];
      console.log('[AUTH DEBUG] Fetched roles:', roles);
      
      // If no roles found and we haven't retried yet, wait and try again
      // This handles the race condition where session token isn't yet attached for RLS
      if (roles.length === 0 && retryCount < 2) {
        console.log('[AUTH DEBUG] No roles found, retrying in 500ms...');
        await new Promise(resolve => setTimeout(resolve, 500));
        return fetchUserRoles(userId, retryCount + 1);
      }
      
      return roles;
    } catch (error) {
      console.error('[AUTH DEBUG] Error fetching user roles:', error);
      return null;
    }
  };

  // Check if user signed in via OAuth (Google, etc.) - they don't need password change
  const isOAuthUser = (currentUser: User | null): boolean => {
    if (!currentUser) return false;
    
    // Check app_metadata for OAuth provider
    const provider = currentUser.app_metadata?.provider;
    const providers = currentUser.app_metadata?.providers;
    
    // If provider is 'google' or providers array contains 'google', it's an OAuth user
    if (provider === 'google' || (Array.isArray(providers) && providers.includes('google'))) {
      return true;
    }
    
    return false;
  };

  const checkPasswordChanged = async (userId: string, currentUser: User | null) => {
    // OAuth users (Google sign-in) don't need to set a password
    if (isOAuthUser(currentUser)) {
      console.log('[AUTH DEBUG] OAuth user detected, skipping password requirement');
      return true; // Return true = password is "changed" / not required
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('password_changed')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking password status:', error);
        // Default to requiring password change on error (safer)
        return false;
      }

      // If no profile found, assume password not changed
      if (!data) {
        console.log('[AUTH DEBUG] No profile found for password check, defaulting to needsChange=true');
        return false;
      }

      return data.password_changed !== false;
    } catch (error) {
      console.error('Error checking password status:', error);
      return false;
    }
  };

  // Check whether the user still needs to accept the current Terms of Use.
  // Also captures the current terms version (id + version number) for the UI.
  const checkTermsAcceptance = async (userId: string): Promise<{ needs: boolean; versionId: string | null; version: number | null }> => {
    try {
      const { data: tv, error: tvErr } = await supabase
        .from('terms_versions')
        .select('id, version')
        .eq('is_current', true)
        .maybeSingle();

      if (tvErr || !tv) {
        console.warn('[AUTH DEBUG] No current terms version found', tvErr);
        return { needs: false, versionId: null, version: null };
      }

      const { data: accept, error: aErr } = await supabase
        .from('terms_acceptances')
        .select('id')
        .eq('user_id', userId)
        .eq('terms_version_id', tv.id)
        .maybeSingle();

      if (aErr) {
        console.error('[AUTH DEBUG] Error checking terms acceptance', aErr);
        return { needs: false, versionId: tv.id, version: tv.version };
      }

      return { needs: !accept, versionId: tv.id, version: tv.version };
    } catch (e) {
      console.error('[AUTH DEBUG] Exception checking terms acceptance', e);
      return { needs: false, versionId: null, version: null };
    }
  };

  // Fetch profile.is_paused for this user (defaults false on error)
  const checkPaused = async (userId: string): Promise<boolean> => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('is_paused')
        .eq('id', userId)
        .maybeSingle();
      return !!(data as any)?.is_paused;
    } catch {
      return false;
    }
  };

  // Handle user data loading (roles + password check + terms acceptance + pause)
  const loadUserData = async (userId: string, currentUser: User | null): Promise<{ roles: UserRole[] | null; passwordChanged: boolean; termsStatus: { needs: boolean; versionId: string | null; version: number | null }; paused: boolean }> => {
    const [roles, passwordChanged, termsStatus, paused] = await Promise.all([
      fetchUserRoles(userId),
      checkPasswordChanged(userId, currentUser),
      checkTermsAcceptance(userId),
      checkPaused(userId),
    ]);
    return { roles, passwordChanged, termsStatus, paused };
  };

  useEffect(() => {
    isMounted.current = true;

    // 1. Set up auth state listener FIRST (for ongoing changes after initial load)

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // Skip if component unmounted
        if (!isMounted.current) return;

        // During initial load, let getSession handle everything
        if (!initialLoadComplete.current) {
          return;
        }
        
        console.log('[AUTH DEBUG] onAuthStateChange event:', event);

        // Handle sign out
        if (!newSession) {
          clearSessionTimeout();
          latestAuthUserIdRef.current = null;
          lastProcessedSessionRef.current = null;
          setSession(null);
          setUser(null);
          setUserRoles([]);
          setNeedsPasswordChange(false);
          return;
        }

        // For USER_UPDATED events (password changes, etc.), just update session/user
        // but DON'T re-fetch roles - this prevents race conditions during password setup
        if (event === 'USER_UPDATED') {
          console.log('[AUTH DEBUG] USER_UPDATED event - updating session only, preserving roles');
          setSession(newSession);
          setUser(newSession.user);
          return;
        }

        // For TOKEN_REFRESHED, just update session silently
        if (event === 'TOKEN_REFRESHED') {
          console.log('[AUTH DEBUG] TOKEN_REFRESHED event - updating session only');
          setSession(newSession);
          setUser(newSession.user);
          return;
        }

        // Synchronous state updates – callback returns immediately
        setSession(newSession);
        setUser(newSession.user);

        // Only fetch roles for SIGNED_IN events
        if (event === 'SIGNED_IN') {
          const sessionToken = newSession.access_token;

          // Deduplicate: skip if we already processed this exact session token
          if (lastProcessedSessionRef.current === sessionToken) {
            console.log('[AUTH DEBUG] SIGNED_IN dedup – already processed this session token, skipping');
            return;
          }

          const userId = newSession.user.id;
          latestAuthUserIdRef.current = userId;

          // Google Ads — detect first-time signup (Google OAuth or otherwise)
          // by comparing created_at vs last_sign_in_at. trackSignupConversion
          // also dedupes per userId via localStorage.
          try {
            const createdAt = newSession.user.created_at;
            const lastSignIn = newSession.user.last_sign_in_at;
            if (createdAt && lastSignIn) {
              const delta = Math.abs(
                new Date(lastSignIn).getTime() - new Date(createdAt).getTime()
              );
              // Within 10s = brand-new account
              if (delta < 10_000) trackSignupConversion(userId);
            }
          } catch {}


          // Defer all async DB work so the callback returns immediately,
          // preventing auth-token lock contention.
          setTimeout(async () => {
            // Stale guard: another auth event may have fired since we were queued
            if (!isMounted.current || latestAuthUserIdRef.current !== userId) {
              console.log('[AUTH DEBUG] Stale SIGNED_IN handler, skipping');
              return;
            }

            console.log('[AUTH DEBUG] SIGNED_IN deferred – fetching user data');
            const { roles, passwordChanged, termsStatus, paused } = await loadUserData(userId, newSession.user);

            // Stale guard after async work
            if (!isMounted.current || latestAuthUserIdRef.current !== userId) {
              console.log('[AUTH DEBUG] Stale after loadUserData, discarding');
              return;
            }

            // Mark this session as processed
            lastProcessedSessionRef.current = sessionToken;

            // If user was deleted, force logout with orphaned session cleanup
            if (roles !== null && roles.length === 0) {
              console.log('[AUTH DEBUG] No roles found in onAuthStateChange, clearing orphaned session');
              setUser(null);
              setSession(null);
              setUserRoles([]);
              setNeedsPasswordChange(false);
              setNeedsTermsAcceptance(false);
              
              try { await supabase.auth.signOut(); } catch (e) {
                console.log('[AUTH DEBUG] SignOut failed (user may be deleted), clearing localStorage');
              }
              
              const keysToRemove: string[] = [];
              for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
                  keysToRemove.push(key);
                }
              }
              keysToRemove.forEach(key => localStorage.removeItem(key));
              
              navigateRef.current('/auth');
              return;
            }

            setUserRoles(roles || []);
            setNeedsPasswordChange(!passwordChanged);
            setNeedsTermsAcceptance(termsStatus.needs);
            setCurrentTermsVersionId(termsStatus.versionId);
            setCurrentTermsVersion(termsStatus.version);
            setIsPaused(paused);
            
            if (newSession.user.last_sign_in_at) {
              startSessionTimeout(newSession.user.last_sign_in_at);
            }
          }, 0);
        }
      }
    );

    // 2. INITIAL LOAD: Get existing session and load all data before setting loading=false
    const initializeAuth = async () => {
      console.log('[AUTH DEBUG] Starting auth initialization...');
      try {
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        
        console.log('[AUTH DEBUG] getSession result:', {
          hasSession: !!existingSession,
          userId: existingSession?.user?.id,
          email: existingSession?.user?.email
        });
        
        if (!isMounted.current) return;

        if (existingSession?.user) {
          setSession(existingSession);
          setUser(existingSession.user);

          // AWAIT all data before setting loading=false
          console.log('[AUTH DEBUG] Loading user data...');
          const { roles, passwordChanged, termsStatus, paused } = await loadUserData(existingSession.user.id, existingSession.user);
          
          console.log('[AUTH DEBUG] User data loaded:', { roles, passwordChanged, termsStatus });
          
          if (!isMounted.current) return;

          // If user was deleted, force logout with orphaned session cleanup
          if (roles !== null && roles.length === 0) {
            console.log('[AUTH DEBUG] No roles found, clearing orphaned session');
            
            // Clear session locally first (signOut may fail for deleted users)
            setUser(null);
            setSession(null);
            setUserRoles([]);
            setNeedsPasswordChange(false);
            setNeedsTermsAcceptance(false);
            
            // Try to sign out (ignore errors for deleted users)
            try {
              await supabase.auth.signOut();
            } catch (e) {
              console.log('[AUTH DEBUG] SignOut failed (user may be deleted), clearing localStorage');
            }
            
            // Clear all Supabase localStorage keys
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
                keysToRemove.push(key);
              }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
            
            setLoading(false);
            initialLoadComplete.current = true;
            navigateRef.current('/auth');
            return;
          }

          setUserRoles(roles || []);
          setNeedsPasswordChange(!passwordChanged);
          setNeedsTermsAcceptance(termsStatus.needs);
          setCurrentTermsVersionId(termsStatus.versionId);
          setCurrentTermsVersion(termsStatus.version);
          setIsPaused(paused);
          
          // Start session timeout based on when user last signed in
          if (existingSession.user.last_sign_in_at) {
            startSessionTimeout(existingSession.user.last_sign_in_at);
          }
          
          console.log('[AUTH DEBUG] Auth state set - roles:', roles);
        } else {
          console.log('[AUTH DEBUG] No existing session found');
          setSession(null);
          setUser(null);
          setUserRoles([]);
          setNeedsPasswordChange(false);
          setNeedsTermsAcceptance(false);
        }
      } catch (error) {
        console.error('[AUTH DEBUG] Error during auth initialization:', error);
      } finally {
        if (isMounted.current) {
          // Mark initial load complete BEFORE setting loading=false
          // This ensures onAuthStateChange knows to start handling events
          initialLoadComplete.current = true;
          setLoading(false);
          console.log('[AUTH DEBUG] Initial load complete, loading=false');
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted.current = false;
      subscription.unsubscribe();
      clearSessionTimeout();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (
    email: string, 
    nombre: string,
    apellidoPaterno: string,
    apellidoMaterno: string,
    recaptchaToken?: string,
  ) => {
    try {
      const currentLang = (i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
      const { data, error } = await supabase.functions.invoke('register-user', {
        body: {
          email,
          nombre,
          apellido_paterno: apellidoPaterno,
          apellido_materno: apellidoMaterno,
          locale: currentLang,
          recaptchaToken,
        },
      });

      if (error) {
        // Try to read JSON body from FunctionsHttpError to surface real error message/code
        try {
          const ctx: any = (error as any).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) {
              const e: any = new Error(body.error);
              e.errorCode = body.errorCode;
              return { error: e };
            }
          }
        } catch {}
        return { error };
      }

      if (data?.error) {
        const e: any = new Error(data.error);
        e.errorCode = data.errorCode;
        return { error: e };
      }

      // Google Ads — fire Sign-up conversion (email signup path)
      try { trackSignupConversion(email); } catch {}

      return { error: null };
    } catch (err: any) {
      return { error: err };
    }
  };

  const signOut = async () => {
    // Tell the route guard this is a deliberate sign-out (see postAuthRedirect).
    (window as any).__iaSigningOut = true;
    try { sessionStorage.removeItem('post_auth_redirect'); } catch { /* private mode */ }
    // Clear state immediately for instant UI feedback
    setUser(null);
    setSession(null);
    setUserRoles([]);
    setNeedsPasswordChange(false);
    setNeedsTermsAcceptance(false);
    clearSessionTimeout();
    
    // Clear Supabase localStorage keys synchronously
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Hard redirect immediately — don't wait for server signout
    window.location.href = '/';
    
    // Fire-and-forget server-side signout (runs before page unloads)
    supabase.auth.signOut().catch(() => {});
  };

  const hasRole = (role: UserRole) => {
    return userRoles.includes(role);
  };

  const isAdminOrStaff = () => {
    return userRoles.includes('admin') || userRoles.includes('staff') || userRoles.includes('instructor') || userRoles.includes('dev');
  };

  const isStudentOrGuest = () => {
    return userRoles.includes('student') || userRoles.includes('guest');
  };

  const getHighestRole = (): UserRole | null => {
    const roleHierarchy: UserRole[] = ['dev', 'admin', 'staff', 'instructor', 'creator', 'student', 'guest'];
    
    for (const role of roleHierarchy) {
      if (userRoles.includes(role)) {
        return role;
      }
    }
    
    return null;
  };

  // Function to manually refresh password status - used after password change
  const refreshPasswordStatus = async () => {
    if (!user?.id) return;
    
    console.log('[AUTH DEBUG] Manually refreshing password status...');
    const passwordChanged = await checkPasswordChanged(user.id, user);
    console.log('[AUTH DEBUG] Password status refreshed:', { passwordChanged });
    
    if (isMounted.current) {
      setNeedsPasswordChange(!passwordChanged);
    }
  };

  // Refresh terms acceptance status — call after the user accepts new terms
  const refreshTermsStatus = async () => {
    if (!user?.id) return;
    const termsStatus = await checkTermsAcceptance(user.id);
    if (isMounted.current) {
      setNeedsTermsAcceptance(termsStatus.needs);
      setCurrentTermsVersionId(termsStatus.versionId);
      setCurrentTermsVersion(termsStatus.version);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        userRoles,
        loading,
        needsPasswordChange,
        needsTermsAcceptance,
        currentTermsVersionId,
        currentTermsVersion,
        isPaused,
        signIn,
        signUp,
        signOut,
        hasRole,
        isAdminOrStaff,
        isStudentOrGuest,
        getHighestRole,
        refreshPasswordStatus,
        refreshTermsStatus,
        isOAuthUser: isOAuthUser(user),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
